import { JobKind } from './job.js';
import { type BaseItem } from '../common.js';
import * as Workflow from '../workflow.js';

export enum Resolution {
  Canceled = 'CANCELED',
  Completed = 'COMPLETED',
  Expired = 'EXPIRED',
  Superseded = 'SUPERSEDED',
}

export enum JobStatus {
  TODO = 'TODO',
  WIP = 'WIP',
  DONE = 'DONE',
  INCOMPLETE = 'INCOMPLETE',
}

interface BaseManualJob {
  jobId: number;
  kind: JobKind;
  ehrInstanceId: string;
  ehrInformationId: string;
  encounterId: string | null;
  patientOrderId: string | null;
  practiceId: string;
  failureReasons: string[] | null;
  jobCreated: Date;
  encounterStartTime: Date | null;
  claimed: Date | null;
  claimedByUserId: string | null;
  expires: Date | null;
  patientOrderEhrScheduled: Date | null;
  status: JobStatus;
  comment: string | null;
}

export interface ManualJob extends BaseManualJob, Omit<BaseItem, 'id'> {}

export type ViewManualJob = ManualJob & {
  // From primary
  practiceGroup: string;
  organization: string;
  startTime: Date;
};

export interface ManualJobWithPayload extends ManualJob {
  payload: object;
}

export interface FindAllOptions {
  limit?: number;
  offset?: number;
  jobIds?: number[];
  kinds?: JobKind[];
  ehrInstanceIds?: string[];
  ehrSystemIds?: string[];
  ehrInformationIds?: string[];
  encounterIds?: string[];
  patientOrderIds?: string[];
  practiceIds?: string[];
  failureReasons?: string[] | null;
  manualJobCreatedAfter?: Date;
  manualJobCreatedBefore?: Date;
  jobCreatedAfter?: Date;
  jobCreatedBefore?: Date;
  encounterStartTimeAfter?: Date;
  encounterStartTimeBefore?: Date;
  claimedAfter?: Date;
  claimedBefore?: Date;
  isClaimed?: boolean;
  claimedByUserId?: string;
  expiresAfter?: Date | null;
  expiresBefore?: Date;
  practiceGroupIds?: string[];
  organizationIds?: string[];
  status?: JobStatus;
  comment?: string;
}

interface BaseManualJobHistory extends Omit<BaseManualJob, 'expires'> {
  manualJobCreated: Date;
  resolution: Resolution;
  resolvedByUserId: string | null;
  supersededByJobId: number | null;
}

export interface ManualJobHistory extends BaseManualJobHistory, Omit<BaseItem, 'id'> {}

export interface FindAllHistoryOptions extends Omit<
  FindAllOptions,
  | 'claimedAfter'
  | 'claimedBefore'
  | 'isClaimed'
  | 'claimedByUserId'
  | 'expiresAfter'
  | 'expiresBefore'
> {
  resolvedAfter?: Date;
  resolvedBefore?: Date;
  resolutions?: Resolution[];
}

export const WORKFLOW_KIND_BY_JOB_KIND_TO_RETRY: {
  [key in JobKind]?: Workflow.Kind;
} = {
  // Solution jobs
  [JobKind.ExportAuthorizationStatus]: Workflow.ExportAuthorizationStatus.Kind,
  [JobKind.ExportAuthorizationSubmission]: Workflow.ExportAuthorizationSubmission.Kind,
  [JobKind.ExtractAuthorizationStatus]: Workflow.ExtractAuthorizationStatus.Kind,
  [JobKind.ExtractPatientAdvisoryDocuments]: Workflow.ExtractPatientAdvisoryDocuments.Kind,
  // RPA jobs
  [JobKind.ExportAllergies]: Workflow.ExportAllergies.Kind,
  [JobKind.ExportAppointmentNote]: Workflow.ExportAppointmentNote.Kind,
  [JobKind.ExportNewEncounter]: Workflow.ExportNewEncounter.Kind,
  [JobKind.ExportEncounterInformation]: Workflow.ExportEncounterInformation.Kind,
  [JobKind.ExportFamilyMedicalHistory]: Workflow.ExportFamilyMedicalHistory.Kind,
  [JobKind.ExportGeneralComment]: Workflow.ExportGeneralComment.Kind,
  [JobKind.ExportGuarantors]: Workflow.ExportGuarantor.Kind,
  [JobKind.ExportMedications]: Workflow.ExportMedications.Kind,
  [JobKind.ExportPatientInformation]: Workflow.ExportPatientInformation.Kind,
  [JobKind.ExportPharmacy]: Workflow.ExportPatientPharmacies.Kind,
  [JobKind.ExportPcp]: Workflow.ExportPatientCareProviders.Kind,
  [JobKind.ExportPreregistrationEncounter]: Workflow.ExportPreregistrationEncounter.Kind,
  [JobKind.ExportVerifyRegistration]: Workflow.ExportVerifyRegistration.Kind,
  [JobKind.ExportProcessedPayments]: Workflow.ExportProcessedPayments.Kind,
};
